import { useCallback, useEffect, useRef, useState } from "react";
import { getSupabase } from "../lib/supabase";
import type { TerminalOrder } from "../lib/types";

export interface OrdersState {
  orders: TerminalOrder[];
  loading: boolean;
  error: string | null;
  loadedOnce: boolean;
}

// Lädt die sichere Server-View merchant_terminal_orders (RLS: nur eigener
// Händler). Der Client bildet KEINE Businessregel nach — er zeigt nur den
// Serverzustand. reload() ist gegen parallele Aufrufe koalesziert.
export function useMerchantOrders(merchantId: string | null) {
  const [state, setState] = useState<OrdersState>({ orders: [], loading: false, error: null, loadedOnce: false });
  const inFlight = useRef(false);
  const pending = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    if (!merchantId) return;
    if (inFlight.current) { pending.current = true; return; } // koaleszieren
    inFlight.current = true;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data, error } = await getSupabase()
        .from("merchant_terminal_orders")
        .select("*")
        .eq("merchant_id", merchantId)
        .order("created_at", { ascending: false });
      if (!mounted.current) return;
      if (error) {
        setState((s) => ({ ...s, loading: false, error: "LOAD_FAILED" }));
      } else {
        setState({ orders: (data ?? []) as TerminalOrder[], loading: false, error: null, loadedOnce: true });
      }
    } catch {
      if (mounted.current) setState((s) => ({ ...s, loading: false, error: "LOAD_FAILED" }));
    } finally {
      inFlight.current = false;
      if (pending.current) { pending.current = false; void reload(); } // aufgestauten Refresh nachziehen
    }
  }, [merchantId]);

  return { ...state, reload };
}
